import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, Typography, Button, Box, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ProjectManager from '../ProjectManager/ProjectManager'
import TaskProgressBar from '../ProgressBar/TaskProgressBar'
import SubmitWorkModal from '../Modal/SubmitWorkModal'
import EditTaskModal from '../Modal/EditTaskModal'
import AdxToolTip from 'components/custom/AdxToolTip'
import { useDeleteTask } from '../../../../services/api/task-management/trackerHooks'
import { TASK_STATUS } from '../../helper/taskStatuses';

export const hasActiveTasks = (project) =>
  project.tasks?.some(task => [TASK_STATUS.ONGOING, TASK_STATUS.PENDING, TASK_STATUS.RETURNED_FOR_REWORK].includes(task.status))

const TaskProjectCard = ({ manager, avatar, projects, onAddTask, onViewCompleted, onTaskUpdate, setSuccessMessage, setErrorMessage }) => {

  const navigate = useNavigate()

  const [hoveredTask, setHoveredTask] = useState(null)
  const [expandedProjects, setExpandedProjects] = useState({})
  const [submitModalOpen, setSubmitModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedTaskData, setSelectedTaskData] = useState(null)
  // Store selected project for submit modal
  const [selectedProject, setSelectedProject] = useState(null)
  // Delete confirmation modal state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState(null)
  // Delete task mutation
  const { mutate: deleteTask, isLoading: isDeleting } = useDeleteTask()
  // const handleAddTask = (projectId) => {
  //   if (onAddTask) {
  //     onAddTask(projectId)
  //   }
  // }
  const handleViewCompleted = (projectId) => {
    // Navigate to My Reviews tab
    navigate('/app/tasks-dashboard/my-tasks', {
      state: {
        flash: {
          type: 'viewCompleted',
          projectId: projectId,
        },
      },
    })
  }
  // Submit handler now takes project only
  const handleSubmitTask = (project) => {
    setSelectedProject(project)  // Store entire project
    setSubmitModalOpen(true)
  }

  const toggleProjectTasks = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }))
  }

  // EDIT HANDLER
  const handleEditTask = (project, task, taskIndex) => {
    setSelectedTaskData({
      taskId: task.id, // Pass the real task ID
      projectId: project.id,
      projectName: project.title,
      taskName: task.name,
      taskIndex: taskIndex,
      value: task.value
    })
    setEditModalOpen(true)
  }

  const handleSaveEdit = (editedData) => {
    console.log('Edited:', editedData)
    // Notify page-level banner via parent
    if (setSuccessMessage) {
      setSuccessMessage('Task updated successfully.')
      // Auto-hide message after 5 seconds (fallback)
      setTimeout(() => setSuccessMessage(''), 5000)
    }
    if (onTaskUpdate) {
      onTaskUpdate('edit', editedData)
    }
    setEditModalOpen(false)
  }

  // DELETE HANDLER
  const handleDeleteTask = (task) => {
    // Store task info and open confirmation modal
    setTaskToDelete(task)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = () => {
    if (taskToDelete) {
      deleteTask(taskToDelete.id, {
        onSuccess: () => {
          // Notify page-level banner via parent
          if (setSuccessMessage) {
            setSuccessMessage('Task deleted successfully.')
            // Auto-hide message after 5 seconds (fallback)
            setTimeout(() => setSuccessMessage(''), 5000)
          }
          // Close modal and reset state
          setDeleteConfirmOpen(false)
          setTaskToDelete(null)
        },
        onError: (error) => {
          console.error('Error deleting task:', error)

          // Notify parent of error for page-level banner
          const errorMsg = error?.response?.data?.message || 'Failed to delete task. Please try again.'
          if (setErrorMessage) {
            setErrorMessage(errorMsg)
            setTimeout(() => setErrorMessage(''), 5000)
          }

          // Close modal
          setDeleteConfirmOpen(false)
          setTaskToDelete(null)
        }
      })
    }
  }

  const cancelDelete = () => {
    setDeleteConfirmOpen(false)
    setTaskToDelete(null)
  }

  // Render project info content for tooltip
  const renderProjectInfo = (project) => (
    <Box sx={{ p: 2, minWidth: 250, maxWidth: 300 }}>
      {/* Project Title */}
      <Typography variant="h4" fontWeight={600} color="#4a4a4a" mb={1} sx={{ fontSize: '16px' }}>
        {project.title}
      </Typography>

      {/* Manager */}
      <Typography fontSize="12px" color='text.secondary' mb={1} display="block">
        Managed by {manager}
      </Typography>

      <Divider sx={{ mb: 1 }} />

      {/* Objective Section */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        mb={0.5}
        color="text.secondary"
        sx={{ fontSize: '12px', textTransform: 'uppercase' }}
      >
        Objective
      </Typography>
      <Typography
        variant="body2"
        mb={2}
        sx={{
          fontSize: '13px',
          lineHeight: 1.5,
          color: project.objective ? 'text.primary' : 'text.disabled'
        }}
      >
        {project.objective || 'N/A'}
      </Typography>

      {/* Description Section */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        mb={0.5}
        color="text.secondary"
        sx={{ fontSize: '12px', textTransform: 'uppercase' }}
      >
        Description
      </Typography>
      <Typography
        variant="body2"
        mb={2}
        sx={{
          fontSize: '13px',
          lineHeight: 1.5,
          color: project.description ? 'text.primary' : 'text.disabled'
        }}
      >
        {project.description || 'N/A'}
      </Typography>

      {/* Link Section */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        mb={0.5}
        color="text.secondary"
        sx={{ fontSize: '12px', textTransform: 'uppercase' }}
      >
        Link
      </Typography>
      {project.link ? (
        <Typography
          component="a"
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            fontSize: '13px',
            color: '#2F80ED',
            textDecoration: 'none',
            wordBreak: 'break-all',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {project.link}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontSize: '13px',
            color: 'text.disabled'
          }}
        >
          N/A
        </Typography>
      )}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: 'calc(100vh - 80px)' }}>

      {/* Project Manager - Outside Card */}
      <Box sx={{ mb: 1, flexShrink: 0 }}>
        <ProjectManager name={manager} avatar={avatar} />
      </Box>

      {/* per-project card banner removed to avoid duplicate messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          borderRadius: 2.5,
          border: 1,
          borderColor: '#e5e5e5',
          backgroundColor: '#ffffff',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#d0d0d0',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#b0b0b0'
            }
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '3px'
          }
        }}
      >
        {/* Empty state when "You" has no projects assigned yet */}
        {projects.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 120,
              p: 3,
            }}
          >
            <Typography fontSize="13px" color="text.disabled" textAlign="center">
              No projects assigned yet.
            </Typography>
          </Box>
        ) : (
          <>
            {projects.map((project) => (
              <Card
                key={project.id}
                sx={{
                  border: 1,
                  borderColor: '#e5e5e5',
                  backgroundColor: '#F6FBFF',
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  mb: 2,
                  '&:last-child': { mb: 0 },
                }}
              >

                <CardContent sx={{ p: 3 }}>
                  {/*
                    Keep task expansion local to each project card so My Tasks matches
                    the Project Dashboard behavior without affecting the page-level data.
                  */}
                  {(() => {
                    const showMoreTasks = !!expandedProjects[project.id]
                    const visibleTasks = showMoreTasks ? project.tasks : project.tasks.slice(0, 5)

                    return (
                      <>
                  {/* Project Title */}
                  <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" mb="2">
                    <Typography
                      fontWeight={600}
                      sx={{
                        fontSize: '16px',
                        mb: '8px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 'calc(100% - 30px)',
                      }}
                    >
                      {project.title}
                    </Typography>

                    {/* Info Icon with Tooltip */}
                    <AdxToolTip
                      title={renderProjectInfo(project)}
                      placement="right"
                      // arrow
                      //Add custom styling via componentsProps
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#ffffff',
                            color: '#000',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            borderRadius: 3,
                            p: 0,
                            maxWidth: 250,
                            border: '1px solid #e5e5e5',
                            '& .MuiTooltip-arrow': {
                              color: '#ffffff',
                              '&::before': {
                                border: '1px solid #e5e5e5'
                              }
                            }
                          }
                        }
                      }}
                    >
                      <Box
                        sx={{
                          color: 'text.secondary',
                          transition: 'color 0.2s ease',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          '&:hover': {
                            color: '#2F80ED',
                          },
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 17 17"
                          fill="currentColor"
                        >
                          <path d="M7.5 4.16667H9.16667V5.83333H7.5V4.16667ZM7.5 7.5H9.16667V12.5H7.5V7.5ZM8.33333 0C3.73333 0 0 3.73333 0 8.33333C0 12.9333 3.73333 16.6667 8.33333 16.6667C12.9333 16.6667 16.6667 12.9333 16.6667 8.33333C16.6667 3.73333 12.9333 0 8.33333 0ZM8.33333 15C4.65833 15 1.66667 12.0083 1.66667 8.33333C1.66667 4.65833 4.65833 1.66667 8.33333 1.66667C12.0083 1.66667 15 4.65833 15 8.33333C15 12.0083 12.0083 15 8.33333 15Z" />
                        </svg>
                      </Box>
                    </AdxToolTip>
                  </Box>

                  {/* Task List */}
                  <Box sx={{ borderRadius: 2, border: 1, borderColor: 'transparent' }}>
                    {project.tasks.length === 0 ? (
                      <Box
                        sx={{
                          border: '1px solid #E5E5E5',
                          borderRadius: 2.5,
                          backgroundColor: '#FFFFFF',
                          px: 3,
                          py: 4,
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 32 32" fill="none">
                            <path
                              d="M25.6663 18.666V10.666C25.6663 6.89535 25.6663 5.00868 24.4943 3.83802C23.3237 2.66602 21.437 2.66602 17.6663 2.66602H12.333C8.56234 2.66602 6.67567 2.66602 5.50501 3.83802C4.33301 5.00868 4.33301 6.89535 4.33301 10.666V21.3327C4.33301 25.1033 4.33301 26.99 5.50501 28.1607C6.67567 29.3327 8.56234 29.3327 12.333 29.3327H17.6663"
                              stroke="#8A8A8A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.66602 19.9993H14.9993M9.66602 14.666H20.3327M28.3327 22.666L24.9993 25.9993M24.9993 25.9993L21.666 29.3327M24.9993 25.9993L28.3327 29.3327M24.9993 25.9993L21.666 22.666M19.666 2.66602H10.3327C10.3327 4.55135 10.3327 5.49402 10.9193 6.07935C11.5047 6.66602 12.4473 6.66602 14.3327 6.66602H15.666C17.5513 6.66602 18.494 6.66602 19.0793 6.07935C19.666 5.49402 19.666 4.55135 19.666 2.66602Z"
                              stroke="#8A8A8A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Box>

                        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#3A3A3A', mb: 1 }}>
                          No tasks yet
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#3A3A3A',
                            maxWidth: 240,
                            lineHeight: 1.6,
                            mb: 2.5,
                          }}
                        >
                          Create your first task to start tracking progress on this project.
                        </Typography>

                        <Button
                          onClick={() => onAddTask?.(project.id)}
                          startIcon={<AddIcon sx={{ fontSize: 22 }} />}
                          sx={{
                            textTransform: 'none',
                            color: '#2F6FE4',
                            fontSize: '14px',
                            fontWeight: 500,
                            p: 0,
                            minWidth: 'auto',
                            '&:hover': {
                              bgcolor: 'transparent',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Create Task
                        </Button>
                      </Box>
                    ) : (
                      <>
                        {visibleTasks.map((task, taskIndex) => {
                          const isAwaitingReview = task.status === TASK_STATUS.SUBMITTED_FOR_REVIEW
                          const isCompleted = task.status === TASK_STATUS.COMPLETED
                          const isSubmitted = isAwaitingReview || isCompleted
                          const isRejected = task.status === TASK_STATUS.RETURNED_FOR_REWORK
                          const hoverKey = `${project.id}-${task.id}`

                          return (
                            <Box
                              key={task.id}
                              onMouseEnter={() => !isSubmitted && setHoveredTask(hoverKey)}
                              onMouseLeave={() => setHoveredTask(null)}
                              sx={{
                                border: 1,
                                borderRadius: 2,
                                borderColor: isRejected ? '#d32f2f' : '#e5e5e5',
                                borderWidth: isRejected ? 2 : 1,
                                mb: 2,
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 2,
                                py: 1.5,
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: 'none',
                                opacity: isSubmitted ? 0.6 : 1,
                                cursor: isSubmitted ? 'not-allowed' : 'default',
                                backgroundColor: isRejected ? '#ffebee' : '#ffffff',
                                '&:hover': {
                                  borderColor: isSubmitted ? '#e5e5e5' : isRejected ? '#d32f2f' : '#2666be',
                                  backgroundColor: isSubmitted ? 'transparent' : isRejected ? '#ffcdd2' : '#f8fafc'
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 2,
                                }}>
                                <Typography sx={{ fontSize: '14px' }}>{task.name}</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#3a3a3a' }}>
                                  {task.value}
                                </Typography>
                              </Box>

                              {!isSubmitted && hoveredTask === hoverKey && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    right: 16,
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    gap: 1,
                                    zIndex: 10,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    top: '50%'
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditTask(project, task, taskIndex)}
                                    sx={{
                                      color: '#3A3A3A',
                                      px: 0.5,
                                      py: 0.5,
                                      bgcolor: '#ffffff',
                                      borderRadius: 1,
                                      border: '1px solid #E0E0E0',
                                      '&:hover': { backgroundColor: '#F5F5F5' }
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 12 12" fill="none">
                                      <path d="M3.3717 8.19432L5.4973 8.1871L10.1367 3.592C10.3188 3.40993 10.419 3.16813 10.419 2.91092C10.419 2.65371 10.3188 2.41192 10.1367 2.22985L9.37279 1.46592C9.00865 1.10178 8.37333 1.10371 8.01208 1.46448L3.3717 6.06054V8.19432ZM8.69171 2.147L9.45708 2.90948L8.68786 3.67147L7.92394 2.90803L8.69171 2.147ZM4.33504 6.46225L7.23949 3.58526L8.00341 4.34918L5.09944 7.22521L4.33504 7.22762V6.46225Z" fill="#3A3A3A" />
                                      <path d="M2.40828 10.1153H9.15161C9.68289 10.1153 10.1149 9.68326 10.1149 9.15198V4.97689L9.15161 5.94023V9.15198H3.92938C3.91686 9.15198 3.90385 9.1568 3.89133 9.1568C3.87544 9.1568 3.85954 9.15246 3.84316 9.15198H2.40828V2.40865H5.70625L6.66958 1.44531H2.40828C1.877 1.44531 1.44495 1.87737 1.44495 2.40865V9.15198C1.44495 9.68326 1.877 10.1153 2.40828 10.1153Z" fill="#3A3A3A" />
                                    </svg>
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteTask(task)}
                                    sx={{
                                      color: '#3A3A3A',
                                      px: 0.5,
                                      py: 0.5,
                                      bgcolor: '#ffffff',
                                      borderRadius: 1,
                                      border: '1px solid #E0E0E0',
                                      '&:hover': { backgroundColor: '#F5F5F5' }
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 12 12" fill="none">
                                      <path d="M9.42495 2.35711C9.54993 2.35711 9.66979 2.40676 9.75817 2.49514C9.84655 2.58351 9.8962 2.70338 9.8962 2.82836C9.8962 2.95334 9.84655 3.07321 9.75817 3.16158C9.66979 3.24996 9.54993 3.29961 9.42495 3.29961H8.9537L8.95228 3.33307L8.51261 9.49278C8.49568 9.73056 8.38928 9.9531 8.21483 10.1156C8.04038 10.278 7.81085 10.3684 7.57246 10.3684H3.73696C3.49857 10.3684 3.26904 10.278 3.09459 10.1156C2.92014 9.9531 2.81374 9.73056 2.79682 9.49278L2.35714 3.33354L2.3562 3.29961H1.88495C1.75996 3.29961 1.6401 3.24996 1.55172 3.16158C1.46335 3.07321 1.4137 2.95334 1.4137 2.82836C1.4137 2.70338 1.46335 2.58351 1.55172 2.49514C1.6401 2.40676 1.75996 2.35711 1.88495 2.35711H9.42495ZM8.00978 3.29961H3.30011L3.73743 9.42586H7.57246L8.00978 3.29961ZM6.59745 0.943359C6.72243 0.943359 6.84229 0.993009 6.93067 1.08139C7.01905 1.16976 7.0687 1.28963 7.0687 1.41461C7.0687 1.53959 7.01905 1.65946 6.93067 1.74783C6.84229 1.83621 6.72243 1.88586 6.59745 1.88586H4.71245C4.58746 1.88586 4.4676 1.83621 4.37922 1.74783C4.29085 1.65946 4.2412 1.53959 4.2412 1.41461C4.2412 1.28963 4.29085 1.16976 4.37922 1.08139C4.4676 0.993009 4.58746 0.943359 4.71245 0.943359H6.59745Z" fill="#3A3A3A" />
                                    </svg>
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          )
                        })}

                        {project.tasks.length > 5 && (
                          <Typography
                            onClick={() => toggleProjectTasks(project.id)}
                            sx={{
                              color: '#2666be',
                              fontWeight: 400,
                              cursor: 'pointer',
                              textAlign: 'center',
                              mb: 2,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {showMoreTasks ? 'View Less' : 'View More'}
                          </Typography>
                        )}

                        <Button
                          startIcon={<DriveFolderUploadIcon width="24" height="24" />}
                          onClick={() => handleSubmitTask(project)}
                          disabled={!hasActiveTasks(project)}
                          sx={{
                            width: '100%',
                            bgcolor: '#FDF6E2',
                            '&:hover': { bgcolor: '#D09A01' },
                            borderRadius: 2,
                            justifyContent: 'center',
                            mb: 2,
                            textTransform: 'none',
                            fontSize: '14px',
                            color: 'text.primary',
                            py: 1,
                            minWidth: 'auto',
                            mt: 2,
                            '&.Mui-disabled': {
                              bgcolor: '#f5f5f5',
                              color: 'rgba(0, 0, 0, 0.26)',
                            },
                          }}
                        >
                          Submit task
                        </Button>

                        <Box sx={{ mb: 2, justifyContent: 'center', display: 'flex' }}>
                          <Button
                            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: '18px' }} />}
                            onClick={() => handleViewCompleted(project.id)}
                            sx={{
                              color: '#2f80ed',
                              textTransform: 'none',
                              minWidth: 'auto',
                              display: 'flex',
                              fontSize: '12px',
                              justifyContent: 'center',
                              alignSelf: 'center',
                              p: 0,
                              '&:hover': { bgcolor: 'transparent' },
                            }}
                          >
                            View Completed ({project.completedCount || 0})
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* Progress Section */}
                  <Box>
                    <TaskProgressBar
                      completedValue={project?.user_deliverable_stats?.completed_value}
                      totalValue={project?.user_deliverable_stats?.total_value}
                      progress={project.progress}
                      status={project.status}
                      pendingCount={project.pendingCount}
                      dueDate={project.dueDate}
                      daysLeft={project.daysLeft}
                    />
                  </Box>
                      </>
                    )
                  })()}
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>

      {/* Submit Work Modal - Now receives projectData */}
      <SubmitWorkModal
        open={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        projectData={selectedProject}  // Pass entire project instead of taskData
        setSuccessMessage={setSuccessMessage}
        setErrorMessage={setErrorMessage}
      />

      {/* Edit Task Modal */}
      <EditTaskModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        taskData={selectedTaskData}
        onSave={handleSaveEdit}
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 3 }}>
          <WarningAmberIcon sx={{ fontSize: 50, color: 'warning.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#333', mt: 1 }}>
            Delete task?
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', px: 4 }}>
          <Typography variant="body1" sx={{ color: '#555', fontSize: '1.1rem', lineHeight: 1.5 }}>
            Are you sure you want to delete this task? <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'column', gap: 1.5, px: 4, pb: 4 }}>
          <Button
            fullWidth
            onClick={confirmDelete}
            variant="contained"
            disabled={isDeleting}
            sx={{
              backgroundColor: 'warning.main',
              color: '#000',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              borderRadius: '100px',
              py: 1.5,
              '&:hover': { backgroundColor: '#D09A01' }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Confirm'}
          </Button>

          <Button
            fullWidth
            onClick={cancelDelete}
            disabled={isDeleting}
            sx={{
              color: '#333',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              py: 1
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TaskProjectCard
